/**
 * NOVAPAY-116-RC2 — Telegram WebApp BackButton policy for Store V2.
 * Root = single home view only → hide BackButton (native close/minimize chrome).
 * All other stack depths → show BackButton (one in-app stack step via handler).
 */

export type StoreV2NavView = { name: string };

export function isStoreV2RootStack(stack: StoreV2NavView[]): boolean {
  return stack.length === 1 && stack[0]?.name === "home";
}

export function shouldShowTelegramBackButton(stack: StoreV2NavView[]): boolean {
  return !isStoreV2RootStack(stack);
}

/** Payret / deep restore must keep home under success so root detection stays correct. */
export function stackWithHomeUnder<T extends StoreV2NavView>(top: T): [{ name: "home" }, T] {
  return [{ name: "home" }, top];
}

type TelegramBackButton = {
  show?: () => void;
  hide?: () => void;
  onClick?: (cb: () => void) => void;
  offClick?: (cb: () => void) => void;
};

function telegramBackButton(): TelegramBackButton | null {
  try {
    return (
      (window as unknown as { Telegram?: { WebApp?: { BackButton?: TelegramBackButton } } }).Telegram
        ?.WebApp?.BackButton ?? null
    );
  } catch {
    return null;
  }
}

/** Immediate hide (e.g. navigateHomeNow) without waiting for React effect. */
export function hideTelegramBackButton(): void {
  try {
    telegramBackButton()?.hide?.();
  } catch {
    /* ignore */
  }
}

export function syncTelegramBackButton(options: {
  stack: StoreV2NavView[];
  onBack: () => void;
}): () => void {
  const button = telegramBackButton();
  if (!button) return () => {};
  const handler = options.onBack;
  if (shouldShowTelegramBackButton(options.stack)) {
    button.show?.();
    button.onClick?.(handler);
  } else {
    try {
      button.offClick?.(handler);
    } catch {
      /* ignore */
    }
    button.hide?.();
  }
  return () => {
    try {
      button.offClick?.(handler);
    } catch {
      /* ignore */
    }
    button.hide?.();
  };
}

export const STORE_V2_BROWSER_CLOSE_HINT_UA =
  "Закрийте цю вкладку або поверніться в Telegram.";
export const STORE_V2_BROWSER_CLOSE_HINT_EN =
  "Close this tab or return to Telegram.";
